import configService from "./configService";
import httpService from "./httpService";
import { getValidatedPostHogDeviceId } from "../utils/posthogDeviceId";
import { getCapturedAppBootProfile } from "../utils/adminCustomerViewBoot";
import { getReferrerOrigin } from "../utils/referrerOrigin";

const PUBLIC_PAGE_HOSTS = new Set(["www.runplutus.com", "app.runplutus.com", "runplutus.com"]);
const CAMPAIGN_PARAMETERS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

interface PublicVisitPage {
  pageType: "home" | "share";
  pageUrl: string;
}

function getPublicVisitPage(pageUrl: string): PublicVisitPage | null {
  const visitUrl = new URL(pageUrl);
  if (visitUrl.protocol !== "https:" || !PUBLIC_PAGE_HOSTS.has(visitUrl.hostname)
    || visitUrl.port || visitUrl.username || visitUrl.password) return null;

  const pageType = visitUrl.pathname === "/" ? "home" : "share";
  if (pageType === "share" && (
    !/^\/(?:picks|product-share)\/[A-Za-z0-9_-]{1,128}\/?$/.test(visitUrl.pathname)
    || /^\/product-share\/dev-preview\/?$/.test(visitUrl.pathname)
  )) return null;

  const campaignParameters = new URLSearchParams();
  for (const parameterName of CAMPAIGN_PARAMETERS) {
    const parameterValue = visitUrl.searchParams.get(parameterName);
    if (parameterValue !== null) campaignParameters.set(parameterName, parameterValue.slice(0, 200));
  }
  // Do not send login credentials, arbitrary query parameters or fragments to the API.
  visitUrl.search = campaignParameters.toString();
  visitUrl.hash = "";
  if (visitUrl.href.length > 2048) return null;
  return { pageType, pageUrl: visitUrl.href };
}

class PublicVisitService {
  async recordVisit(pageUrl: string, referrer: string): Promise<void> {
    try {
      const bootProfile = getCapturedAppBootProfile();
      if (!configService.isProduction() || bootProfile.status !== "valid"
        || bootProfile.profile !== "ordinary") return;
      const visitPage = getPublicVisitPage(pageUrl);
      if (!visitPage) return;
      const anonymousId = getValidatedPostHogDeviceId();
      if (!anonymousId) return;

      await httpService.post("v3/public/visits", {
        anonymousId,
        eventId: crypto.randomUUID(),
        occurredAt: new Date().toISOString(),
        ...visitPage,
        referrer: getReferrerOrigin(referrer),
      }, {
        requiresAuth: false,
        credentials: "omit",
        retryOnUnauthorized: false,
        keepalive: true,
        timeout: 5_000,
      });
    } catch {
      // Best effort: no retries, UI errors, credentials or request payloads in logs.
    }
  }
}

export const publicVisitService = new PublicVisitService();
